import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import oktaAuth from '../../lib/oktaClient';
import { setUser, setToken } from '../../store/features/auth/authSlice';
import { startSession } from '../../lib/usageSession';
import { trackUsageEvent } from '../../lib/usageTracking';
import type { AppDispatch } from '../../store';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
});

// okta-auth-js stores the pre-redirect location (set by signInWithRedirect())
// as an absolute URL. Resolve it back to a router-relative path, falling back
// to '/' for anything missing or cross-origin (defense against a tampered
// stored value being used as an open redirect).
function resolvePostLoginPath(): string {
  const originalUri = oktaAuth.getOriginalUri();
  oktaAuth.removeOriginalUri();
  if (!originalUri) return '/';
  try {
    const url = new URL(originalUri, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return '/';
  }
}

function AuthCallbackPage(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // Prevent React Strict Mode's double-invocation from racing on the same auth code.
  // The ref persists across the mock unmount/remount; the second run exits immediately.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    async function handleCallback(): Promise<void> {
      try {
        // Not a login redirect — IdP post-logout redirect lands here with no code
        if (!oktaAuth.isLoginRedirect()) {
          navigate({ to: '/' });
          return;
        }
        await oktaAuth.handleLoginRedirect();

        const token = oktaAuth.getAccessToken();

        // Dispatch token BEFORE any async work so Apollo has it if anything
        // navigates to / early (e.g. a parallel effect or SSO short-circuit).
        if (token) {
          dispatch(setToken(token));
        }

        const userInfo = await oktaAuth.getUser();

        const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? 'http://localhost:3001';
        let roles: string[] = [];
        let teams: string[] = [];
        if (token) {
          try {
            const res = await fetch(`${API_BASE}/api/v1/users/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const body = (await res.json()) as { data?: { roles?: string[]; teams?: string[] } };
              roles = body.data?.roles ?? [];
              teams = body.data?.teams ?? [];
            }
          } catch {
            /* fall through with empty roles/teams */
          }
        }

        dispatch(
          setUser({
            id: userInfo.sub,
            email: userInfo.email ?? '',
            name: userInfo.name ?? '',
            roles,
            teams,
          })
        );

        // Start a new usage-tracking session and fire the adoption signal
        // exactly once, here, on a real login-redirect completion — not in
        // useOktaAuth.ts's session-rehydration path, which runs on every
        // page load a token is already valid and would otherwise fire
        // session.started once per page load instead of once per login
        // (issue #1450, part of epic #1448).
        startSession();
        void trackUsageEvent('session.started', teams[0] ? { teamId: teams[0] } : {});

        navigate({ to: resolvePostLoginPath() });
      } catch (error) {
        // If tokens are present (e.g. race on a concurrent effect that already
        // completed the exchange), just restore the original deep link.
        if (await oktaAuth.isAuthenticated()) {
          navigate({ to: resolvePostLoginPath() });
          return;
        }
        console.error('Auth callback error:', error);
        // Not authenticated — restart login. Nested try-catch: if signInWithRedirect
        // also fails (discovery CORS, IdP unreachable), force a full-page reload so
        // initAuth() retries from a clean state rather than hanging the spinner.
        try {
          await oktaAuth.signInWithRedirect();
        } catch {
          window.location.assign('/');
        }
      }
    }

    handleCallback().catch(() => {
      window.location.assign('/');
    });
  }, [dispatch, navigate]);

  return (
    <div className="auth-callback">
      <p>Completing sign-in...</p>
    </div>
  );
}
